/**
 * Step 10's launch checklist — the topic registry (ClickUp `86d3ukp23`,
 * Onboarding 11).
 *
 * Same philosophy as `intake-questions.ts`: the content is data, the behaviour
 * is a few pure functions over it, and the API and the dashboard read this one
 * array rather than keeping two copies of the copy in step by hand.
 *
 * The ten topics are verbatim from the Developer Brief v2.0, step 10.
 *
 * ## What an item is, and why the checkbox is the only completion signal
 *
 * The Brief specifies checkboxes and nothing else. Mahala then added, in Slack
 * on 2026-08-07, that "the information they add in these interactive check
 * lists need to be sent to the doer in their dashboard" — so each topic also
 * carries content. Product confirmed the full set: mark done, free text, a
 * document link, or a file upload.
 *
 * Those three enrichments are OPTIONAL and never gate anything. If any of them
 * could also satisfy an item, then "note written but not ticked" would have no
 * defined meaning and step-10 completion would become a rule you have to
 * explain. Keeping `checked` as the sole signal means completion matches the
 * Brief exactly and the PDF has something definite to render.
 *
 * ## The free text is conversation notes, NOT re-collection
 *
 * Four of these topics — brand voice, access and tools, communication
 * expectations, decision boundaries — are already asked in the step-2 intake.
 * Asking again would make founders answer twice and let the two answers
 * disagree. The prompt is what they AGREED WITH THEIR DOER, which is a
 * different question about the same subject.
 */
import { z } from 'zod';
import { webUrlSchema } from './web-urls.js';
/**
 * The ten topics, in the Brief's order.
 *
 * Ids are derived from the titles rather than numbered (`topic_1`) so that
 * reordering the list — or Product inserting an eleventh — cannot silently
 * re-point one founder's saved notes at a different topic.
 */
export const LAUNCH_CHECKLIST_TOPICS = [
    {
        id: 'share_your_vision',
        order: 1,
        title: 'Share your vision',
        prompt: 'Where you want the business to be, and what your Doer is helping you build toward.',
    },
    {
        id: 'walk_through_current_systems',
        order: 2,
        title: 'Walk through current systems',
        prompt: 'The tools and processes you already run on, and how work flows through them today.',
    },
    {
        id: 'communication_expectations',
        order: 3,
        title: 'Communication expectations',
        prompt: 'How and when you two will talk — hours, channels, and what counts as urgent.',
    },
    {
        id: 'brand_voice',
        order: 4,
        title: 'Brand voice',
        prompt: 'How your brand sounds, and anything your Doer should never say on your behalf.',
    },
    {
        id: 'quality_standards',
        order: 5,
        title: 'Quality standards',
        prompt: 'What "good" looks like for you, and what you want to review before it ships.',
    },
    {
        id: 'access_and_tools',
        order: 6,
        title: 'Access and tools',
        prompt: 'The logins and accounts your Doer needs, and how you will share them safely.',
    },
    {
        id: 'decision_boundaries',
        order: 7,
        title: 'Decision boundaries',
        prompt: 'What your Doer can decide alone, and what should always come back to you first.',
    },
    {
        id: 'priorities_and_deadlines',
        order: 8,
        title: 'Priorities and deadlines',
        prompt: 'What matters most in the first few weeks, and anything already date-bound.',
    },
    {
        id: 'feedback_cadence',
        order: 9,
        title: 'Feedback cadence',
        prompt: 'When you will review work together, and how you prefer to give feedback.',
    },
    {
        id: 'encourage_ideas_and_questions',
        order: 10,
        title: 'Encourage ideas and questions',
        prompt: 'How you want your Doer to raise ideas, flag blockers, and ask for what they need.',
    },
];
/** Total topics — exported so tests assert against one number. */
export const LAUNCH_CHECKLIST_TOPIC_COUNT = LAUNCH_CHECKLIST_TOPICS.length;
export function getLaunchChecklistTopic(id) {
    return LAUNCH_CHECKLIST_TOPICS.find((topic) => topic.id === id);
}
/** Longest note we will store. Generous for notes, bounded so metadata stays small. */
export const LAUNCH_CHECKLIST_NOTE_MAX = 2_000;
/**
 * One topic's saved state.
 *
 * `checked` is required and is the completion signal. The rest are optional
 * enrichment, and `null` is how a founder clears one — distinct from `undefined`,
 * which a PATCH uses to mean "leave this alone".
 */
export const launchChecklistItemSchema = z.object({
    checked: z.boolean(),
    note: z.string().max(LAUNCH_CHECKLIST_NOTE_MAX).nullable().optional(),
    link: webUrlSchema.nullable().optional(),
    file_id: z.string().uuid().nullable().optional(),
});
/** The PATCH body: any subset of one item's fields. */
export const launchChecklistPatchSchema = launchChecklistItemSchema.partial().refine((patch) => Object.keys(patch).length > 0, { message: 'Provide at least one of checked, note, link or file_id' });
/**
 * Is every topic checked?
 *
 * A pure question about the checklist itself. It no longer gates step 10 at all
 * — the Launch Day screen's "Next" closes that (ClickUp `86d40tuc8`) — so this
 * feeds the progress meter and the copy, and nothing else.
 */
export function isLaunchChecklistComplete(progress) {
    return LAUNCH_CHECKLIST_TOPICS.every((topic) => progress[topic.id]?.checked === true);
}
/** How many topics are ticked — the progress indicator's numerator. */
export function launchChecklistCheckedCount(progress) {
    return LAUNCH_CHECKLIST_TOPICS.filter((topic) => progress[topic.id]?.checked === true).length;
}
/**
 * Merge one PATCH into one topic's saved state.
 *
 * The whole rule is the two lines below: `undefined` means "leave this alone",
 * an explicit `null` means "clear it". Spreading the patch instead would turn
 * every omitted field into `undefined` and wipe it, which is why this is a
 * function and not four lines of object spread at each caller.
 *
 * Used by BOTH sides — the API's write path and the dashboard's optimistic
 * update — so the two can never drift. Adding a fifth field here changes both
 * at once; two copies would let the optimistic view start disagreeing with what
 * the server stores, silently.
 */
export function applyLaunchChecklistItemPatch(current, patch) {
    const base = current ?? { checked: false };
    return {
        checked: patch.checked ?? base.checked,
        note: patch.note === undefined ? base.note ?? null : patch.note,
        link: patch.link === undefined ? base.link ?? null : patch.link,
        file_id: patch.file_id === undefined ? base.file_id ?? null : patch.file_id,
    };
}
/**
 * Apply one PATCH to a view, returning the view the server will answer with.
 *
 * Shared with the dashboard so its optimistic update is the SAME rule the API
 * applies — the merge itself is `applyLaunchChecklistItemPatch`, which both
 * sides call.
 *
 * Counted over the view's OWN `topics` rather than the bundled registry: the
 * client renders whatever the server sent, so if the two ever disagree the
 * progress it shows must match the list it is showing.
 *
 * `stepStatus` and `complete` are passed through untouched. Nothing a founder
 * ticks can close step 10 any more — the Launch Day screen's "Next" does that
 * (ClickUp `86d40tuc8`) — so an optimistic view that recomputed `complete` from
 * the ticks would flash a congratulation the server is never going to confirm.
 */
export function applyLaunchChecklistPatch(view, topicId, patch) {
    const progress = {
        ...view.progress,
        [topicId]: applyLaunchChecklistItemPatch(view.progress[topicId], patch),
    };
    const checkedCount = view.topics.filter((topic) => progress[topic.id]?.checked === true).length;
    const allChecked = checkedCount === view.topics.length;
    return { ...view, progress, checkedCount, allChecked };
}
//# sourceMappingURL=launch-checklist.js.map